// src/FleetStats.jsx
//
// Story spine (one time window per layer, each labeled):
//   1. Headline sentence + FleetBoard             — "the fleet right now" (drove last 7d / in-field last week)
//   2. Needs attention + trend evidence          — diagnostic (selected date range)
//   3. Details on demand                         — all robots, inactive & idle, movers, per-site, CSV
// Every robot mention is clickable → sets the robot filter (overview → zoom → detail).
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ArrowLeft,
    Download,
    Building2,
    LogOut,
    TrendingUp,
    CalendarDays,
    Bot,
    X,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

/* ---- internal modules ---- */
import { C, PAGE_BG, CARD_BG, CARD_BORDER, getRRIBand, INACTIVE_CUTOFF_DAYS } from "./fleet-stats/constants.js";
import { isoToDate, dateToIso, addDays, daysBetween } from "./fleet-stats/utils/date.js";
import { fmtNum, fmtInt } from "./fleet-stats/utils/format.js";
import { flattenResults } from "./fleet-stats/utils/flattenResults.js";
import { normalizeClientName } from "./fleet-stats/utils/flattenResults.js";
import { classifyAudience, AUDIENCE_OPTIONS } from "./fleet-stats/utils/audience.js";
import { buildDailySeries } from "./fleet-stats/analytics/buildDailySeries.js";
import { detectChartMarkers } from "./fleet-stats/analytics/detectAnomalies.js";
import { buildInsights, buildTrends } from "./fleet-stats/analytics/buildInsights.js";
import { buildRobotHealth } from "./fleet-stats/analytics/robotHealth.js";
import { buildMostChanged } from "./fleet-stats/analytics/mostChanged.js";
import { buildAttentionList } from "./fleet-stats/analytics/buildAttentionList.js";
import { buildHeadline } from "./fleet-stats/analytics/buildHeadline.js";

import {
    Dropdown,
    QuickRangeButton,
    DataTable,
    brandButton,
    hoverNeon,
    unhoverNeon,
} from "./fleet-stats/components/SharedUI.jsx";
import { HeadlineSentence } from "./fleet-stats/components/HeadlineSentence.jsx";
import { FleetBoard } from "./fleet-stats/components/FleetBoard.jsx";
import { AttentionSection } from "./fleet-stats/components/AttentionSection.jsx";
import { TrendCards } from "./fleet-stats/components/TrendCards.jsx";
import { DailyActivityChart } from "./fleet-stats/components/DailyActivityChart.jsx";
import { MostChangedSection } from "./fleet-stats/components/MostChangedSection.jsx";
import { RobotHealthSection } from "./fleet-stats/components/RobotHealthSection.jsx";
import { InactiveRobotsSection } from "./fleet-stats/components/InactiveRobotsSection.jsx";

/* -------- datepicker styles -------- */
const datepickerStyles = `
.wheelme-datepicker { background: ${C.deepBlue}; border: 1px solid ${C.grey3}; border-radius: 14px; color: ${C.offWhite}; font-family: inherit; }
.react-datepicker { background: ${C.deepBlue}; border: 1px solid ${C.grey3}; border-radius: 14px; overflow: hidden; }
.react-datepicker__header { background: ${C.darkBlue}; border-bottom: 1px solid ${C.grey3}; }
.react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header, .react-datepicker__day-name, .react-datepicker__navigation-icon::before { color: ${C.offWhite}; }
.react-datepicker__day { color: ${C.offWhite}; border-radius: 8px; }
.react-datepicker__day:hover { background: rgba(123,255,137,0.12); }
.react-datepicker__day--selected, .react-datepicker__day--keyboard-selected { background: ${C.neonGreen}; color: ${C.darkBlue}; }
.react-datepicker__day--outside-month { color: ${C.grey3}; }
.react-datepicker__triangle { display: none; }
`;

/* -------- narrative section label -------- */
function Overline({ children }) {
    return (
        <div className="text-xs font-semibold uppercase tracking-[0.18em] mb-3" style={{ color: C.grey2 }}>
            {children}
        </div>
    );
}

/* -------- collapsible detail toggle -------- */
function DetailToggle({ open, onToggle, icon: Icon, children }) {
    return (
        <button
            onClick={onToggle}
            className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
            style={{ color: C.grey1 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
        >
            <Icon size={16} />
            {open ? "Hide" : "Show"} {children}
        </button>
    );
}

/* -------- main component -------- */
export default function FleetStats({ onBack, userEmail, onSignOut }) {
    const [raw, setRaw] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // filter state
    const [audience, setAudience] = useState("customer");
    const [clientFilter, setClientFilter] = useState("All");
    const [siteFilter, setSiteFilter] = useState("All");
    const [robotFilter, setRobotFilter] = useState("All");
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [quickRange, setQuickRange] = useState("All");

    // chart state
    const [metric, setMetric] = useState("km");
    const [dragStart, setDragStart] = useState(null);
    const [dragEnd, setDragEnd] = useState(null);

    // visibility toggles
    const [showSiteTable, setShowSiteTable] = useState(false);
    const [inactiveOpen, setInactiveOpen] = useState(false);
    const [inactiveTab, setInactiveTab] = useState("quiet");

    // scroll anchors for click-to-drill navigation
    const attentionRef = useRef(null);
    const inactiveRef = useRef(null);

    /* ---- fetch ---- */
    useEffect(() => {
        fetch(`/robot_stats.json?v=${Date.now()}`, { cache: "no-store" })
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const lm = r.headers.get("last-modified");
                if (lm) setLastUpdated(new Date(lm));
                return r.json();
            })
            .then(setRaw)
            .catch((e) => setLoadError(String(e)));
    }, []);

    /* ---- flatten (internal sites are excluded from every view) ---- */
    const rows = useMemo(
        () => flattenResults(raw).filter((r) => classifyAudience(r.client, r.siteName) !== "internal"),
        [raw]
    );

    /* ---- audience lens: customers vs partners ---- */
    const audienceRows = useMemo(() => {
        if (audience === "all") return rows;
        return rows.filter((r) => classifyAudience(r.client, r.siteName) === audience);
    }, [rows, audience]);

    /* ---- filter options (cascading) ---- */
    const clientOptions = useMemo(() => {
        const s = new Set(audienceRows.map((r) => r.client).filter(Boolean));
        return ["All", ...Array.from(s).sort()];
    }, [audienceRows]);

    const siteOptions = useMemo(() => {
        const filtered = clientFilter === "All" ? audienceRows : audienceRows.filter((r) => r.client === clientFilter);
        const s = new Set(filtered.map((r) => r.siteName).filter(Boolean));
        return ["All", ...Array.from(s).sort()];
    }, [audienceRows, clientFilter]);

    const robotOptions = useMemo(() => {
        let filtered = audienceRows;
        if (clientFilter !== "All") filtered = filtered.filter((r) => r.client === clientFilter);
        if (siteFilter !== "All") filtered = filtered.filter((r) => r.siteName === siteFilter);
        const s = new Set(filtered.map((r) => r.robotName).filter(Boolean));
        return ["All", ...Array.from(s).sort()];
    }, [audienceRows, clientFilter, siteFilter]);

    useEffect(() => { if (!clientOptions.includes(clientFilter)) setClientFilter("All"); }, [clientOptions, clientFilter]);
    useEffect(() => { if (!siteOptions.includes(siteFilter)) setSiteFilter("All"); }, [siteOptions, siteFilter]);
    useEffect(() => { if (!robotOptions.includes(robotFilter)) setRobotFilter("All"); }, [robotOptions, robotFilter]);

    /* ---- scope (before date filter) ---- */
    const scope = useMemo(() => {
        let out = audienceRows;
        if (clientFilter !== "All") out = out.filter((r) => r.client === clientFilter);
        if (siteFilter !== "All") out = out.filter((r) => r.siteName === siteFilter);
        if (robotFilter !== "All") out = out.filter((r) => r.robotName === robotFilter);
        return out;
    }, [audienceRows, clientFilter, siteFilter, robotFilter]);

    /* ---- date bounds ---- */
    const dateBounds = useMemo(() => {
        if (scope.length === 0) return { min: null, max: null };
        let min = scope[0].date, max = scope[0].date;
        for (const r of scope) { if (r.date < min) min = r.date; if (r.date > max) max = r.date; }
        return { min, max };
    }, [scope]);

    useEffect(() => {
        if (!dateBounds.min || !dateBounds.max) return;
        // Default to the last 30 days — "how we got here" should compare
        // recent periods, not the entire dataset against prehistory.
        setDateFrom((d) => {
            if (d && d >= dateBounds.min && d <= dateBounds.max) return d;
            const def = addDays(dateBounds.max, -29);
            return def < dateBounds.min ? dateBounds.min : def;
        });
        setDateTo((d) => (d && d >= dateBounds.min && d <= dateBounds.max ? d : dateBounds.max));
    }, [dateBounds.min, dateBounds.max]);

    useEffect(() => {
        if (!dateBounds.min || !dateBounds.max || !dateFrom || !dateTo) return;
        const presets = [
            { label: "7d", from: addDays(dateBounds.max, -6), to: dateBounds.max },
            { label: "30d", from: addDays(dateBounds.max, -29), to: dateBounds.max },
            { label: "90d", from: addDays(dateBounds.max, -89), to: dateBounds.max },
            { label: "1y", from: addDays(dateBounds.max, -364), to: dateBounds.max },
            { label: "All", from: dateBounds.min, to: dateBounds.max },
        ].map((p) => ({ ...p, from: p.from < dateBounds.min ? dateBounds.min : p.from }));
        const matched = presets.find((p) => p.from === dateFrom && p.to === dateTo);
        setQuickRange(matched ? matched.label : "");
    }, [dateFrom, dateTo, dateBounds.min, dateBounds.max]);

    /* ---- date-filtered view ---- */
    const view = useMemo(() => {
        if (!dateFrom || !dateTo) return scope;
        return scope.filter((r) => r.date >= dateFrom && r.date <= dateTo);
    }, [scope, dateFrom, dateTo]);

    /* ---- analytics ---- */
    const maxDate = dateBounds.max;

    // Insights (run on scoped rows, not date-filtered — needs history)
    const insights = useMemo(() => buildInsights(scope, maxDate), [scope, maxDate]);

    // Trends (current period vs previous)
    const trends = useMemo(() => buildTrends(scope, dateFrom, dateTo), [scope, dateFrom, dateTo]);

    // Robot health (run on scope, needs history)
    const healthMap = useMemo(() => buildRobotHealth(scope, maxDate), [scope, maxDate]);

    // Most changed robots
    const mostChanged = useMemo(() => buildMostChanged(scope, maxDate), [scope, maxDate]);

    /* ---- robot meta (id → display fields, one entry per robot in scope) ---- */
    const robotMeta = useMemo(() => {
        const m = new Map();
        for (const r of scope) {
            if (!m.has(r.robotId)) m.set(r.robotId, { robotName: r.robotName, client: r.client || "Unknown", siteName: r.siteName });
        }
        return m;
    }, [scope]);

    /* ---- attention list ---- */
    const attention = useMemo(() => buildAttentionList(insights, healthMap, scope, maxDate), [insights, healthMap, scope, maxDate]);
    // Coherent story numbers: "needs attention" counts in-field robots only;
    // recently-offline is its own clause; standing issues are archive.
    const attentionInField = useMemo(() => attention.filter((e) => e.severity !== "info" && e.kind === "infield"), [attention]);
    const recentlyOffline = useMemo(() => attention.filter((e) => e.severity !== "info" && e.kind === "offline"), [attention]);
    const infoAttention = useMemo(() => attention.filter((e) => e.severity === "info"), [attention]);
    const clientTrendFindings = useMemo(() => insights.filter((f) => f.type === "trend"), [insights]);
    const positiveFindings = useMemo(() => insights.filter((f) => f.severity === "positive"), [insights]);

    // Robots "in field": any data (uptime or driving) within the last week of
    // the dataset. Cutoff mirrors the offline finding exactly, so in-field and
    // offline are complements — the story's numbers stay coherent.
    const fieldRobotCount = useMemo(() => {
        if (!maxDate) return 0;
        const cutoff = addDays(maxDate, -INACTIVE_CUTOFF_DAYS);
        const s = new Set();
        for (const r of scope) {
            if (r.date >= cutoff && ((r.uptime_s || 0) > 0 || r.distance_m > 0 || r.moving_time_s > 0)) s.add(r.robotId);
        }
        return s.size;
    }, [scope, maxDate]);

    // Fixed last-7-days window vs previous 7 days — "now" layer, independent of the date filter
    const heroTrends = useMemo(() => {
        if (!maxDate) return null;
        return buildTrends(scope, addDays(maxDate, -6), maxDate);
    }, [scope, maxDate]);

    const fleetHealth = useMemo(() => {
        const scores = Array.from(healthMap.values()).map((h) => h.rri).filter((v) => v !== null).sort((a, b) => a - b);
        if (scores.length === 0) return { median: null, scored: 0, band: getRRIBand(null) };
        const mid = Math.floor(scores.length / 2);
        const median = scores.length % 2 === 0 ? Math.round((scores[mid - 1] + scores[mid]) / 2) : scores[mid];
        return { median, scored: scores.length, band: getRRIBand(median) };
    }, [healthMap]);

    /* ---- aggregations ---- */
    const totals = useMemo(() => {
        const robotSet = new Set();
        for (const r of view) robotSet.add(r.robotId);
        return { robots: robotSet.size };
    }, [view]);

    /* ---- per-robot (feeds All-robots table + CSV) ---- */
    const perRobot = useMemo(() => {
        const byRobot = new Map();
        for (const r of view) {
            let e = byRobot.get(r.robotId);
            if (!e) { e = { robotId: r.robotId, robotName: r.robotName, client: r.client, siteName: r.siteName, distance_m: 0, moving_time_s: 0, uptime_s: 0, activeDays: 0, firstActive: null, lastActive: null }; byRobot.set(r.robotId, e); }
            e.distance_m += r.distance_m; e.moving_time_s += r.moving_time_s; e.uptime_s += (r.uptime_s || 0);
            if (r.distance_m > 0 || r.moving_time_s > 0) {
                e.activeDays++;
                if (!e.firstActive || r.date < e.firstActive) e.firstActive = r.date;
                if (!e.lastActive || r.date > e.lastActive) e.lastActive = r.date;
            }
        }
        return Array.from(byRobot.values()).sort((a, b) => b.distance_m - a.distance_m);
    }, [view]);

    /* ---- per-site ---- */
    const perSite = useMemo(() => {
        const bySite = new Map();
        for (const r of view) {
            let e = bySite.get(r.siteName);
            if (!e) { e = { siteName: r.siteName, client: r.client, distance_m: 0, moving_time_s: 0, robotSet: new Set(), firstActive: null, lastActive: null }; bySite.set(r.siteName, e); }
            e.distance_m += r.distance_m; e.moving_time_s += r.moving_time_s; e.robotSet.add(r.robotId);
            if (r.distance_m > 0 || r.moving_time_s > 0) {
                if (!e.firstActive || r.date < e.firstActive) e.firstActive = r.date;
                if (!e.lastActive || r.date > e.lastActive) e.lastActive = r.date;
            }
        }
        return Array.from(bySite.values()).map((e) => ({ ...e, robots: e.robotSet.size, kmPerRobot: e.robotSet.size ? e.distance_m / e.robotSet.size / 1000 : 0 })).sort((a, b) => b.distance_m - a.distance_m);
    }, [view]);

    /* ---- inactive robots (in scope but zero activity in date-filtered view) ---- */
    const inactiveRobots = useMemo(() => {
        const activeInView = new Set(
            view.filter((r) => r.distance_m > 0 || r.moving_time_s > 0).map((r) => r.robotId)
        );
        const allInScope = new Map();
        for (const r of scope) {
            if (!allInScope.has(r.robotId)) allInScope.set(r.robotId, { robotId: r.robotId, robotName: r.robotName, siteName: r.siteName, client: r.client });
        }
        return [...allInScope.values()].filter((r) => !activeInView.has(r.robotId));
    }, [scope, view]);

    /* ---- live robots (drove within the last 7 days of the dataset) ---- */
    const liveRobots = useMemo(() => {
        const scopeActive = scope.filter((r) => r.distance_m > 0 || r.moving_time_s > 0);
        if (scopeActive.length === 0) return { latest: null, cutoff: null, rows: [], count: 0 };
        let latest = scopeActive.reduce((best, r) => (r.date > best ? r.date : best), scopeActive[0].date);
        const cutoff = addDays(latest, -6);
        const live = scopeActive.filter((r) => r.date >= cutoff);
        const byRobot = new Map();
        for (const r of live) {
            let e = byRobot.get(r.robotId);
            if (!e) { e = { robotId: r.robotId, robotName: r.robotName, client: r.client, siteName: r.siteName, distance_m: 0, moving_time_s: 0, lastSeen: null }; byRobot.set(r.robotId, e); }
            e.distance_m += r.distance_m; e.moving_time_s += r.moving_time_s;
            if (!e.lastSeen || r.date > e.lastSeen) e.lastSeen = r.date;
        }
        const liveRows = Array.from(byRobot.values()).map((e) => ({ ...e, kmLast7d: e.distance_m / 1000, hoursLast7d: e.moving_time_s / 3600 }));
        return { latest, cutoff, rows: liveRows, count: liveRows.length };
    }, [scope]);

    /* ---- fleet board (in-field robots as one flat chip grid) ---- */
    const boardData = useMemo(() => {
        if (!maxDate) return { latest: null, cutoff: null, robots: [], offlineTotal: 0 };
        const fieldCutoff = addDays(maxDate, -INACTIVE_CUTOFF_DAYS);
        const inField = new Set();
        // last day each robot actually DROVE (not just uptime) — the chip's
        // "Nd quiet" label promises days since last drive
        const lastDroveById = new Map();
        for (const r of scope) {
            if (r.date >= fieldCutoff && ((r.uptime_s || 0) > 0 || r.distance_m > 0 || r.moving_time_s > 0)) inField.add(r.robotId);
            if (r.distance_m > 0 || r.moving_time_s > 0) {
                const prev = lastDroveById.get(r.robotId);
                if (!prev || r.date > prev) lastDroveById.set(r.robotId, r.date);
            }
        }
        const activeById = new Map(liveRobots.rows.map((r) => [r.robotId, r]));
        const attnById = new Map(attentionInField.map((e) => [e.robotId, e.severity]));

        const robots = [];
        let offlineTotal = 0;
        for (const [robotId, meta] of robotMeta.entries()) {
            if (!inField.has(robotId)) { offlineTotal++; continue; }
            const live = activeById.get(robotId);
            const h = healthMap.get(robotId);
            const lastDrove = lastDroveById.get(robotId) || null;
            robots.push({
                robotId,
                robotName: meta.robotName,
                client: meta.client,
                siteName: meta.siteName,
                active: !!live,
                km7d: live ? live.kmLast7d : null,
                daysQuiet: !live && lastDrove ? daysBetween(lastDrove, maxDate) : null,
                lastDrove,
                rri: h?.rri ?? null,
                rriLabel: h?.label || null,
                attention: attnById.get(robotId) || null,
            });
        }
        const sevRank = { critical: 0, warning: 1 };
        robots.sort((a, b) => {
            const ra = sevRank[a.attention] ?? 2, rb = sevRank[b.attention] ?? 2;
            if (ra !== rb) return ra - rb;
            if (a.active !== b.active) return a.active ? -1 : 1;
            if (a.active) return (b.km7d || 0) - (a.km7d || 0);
            return (a.daysQuiet ?? 999) - (b.daysQuiet ?? 999);
        });
        return {
            latest: liveRobots.latest || maxDate,
            cutoff: liveRobots.cutoff || addDays(maxDate, -1),
            robots,
            offlineTotal,
        };
    }, [robotMeta, scope, liveRobots, healthMap, attentionInField, maxDate]);

    /* ---- headline sentence (the page's summary layer) ---- */
    const headline = useMemo(() => buildHeadline({
        liveRobots,
        fieldRobotCount,
        inFieldAttention: attentionInField,
        fleetHealth,
        heroTrends,
        maxDate,
        todayIso: new Date().toISOString().slice(0, 10),
    }), [liveRobots, fieldRobotCount, attentionInField, fleetHealth, heroTrends, maxDate]);

    /* ---- offline robots (no data at all for >7 days) ---- */
    const offlineRobots = useMemo(() => {
        if (!maxDate) return [];
        const cutoff = addDays(maxDate, -INACTIVE_CUTOFF_DAYS);
        const out = [];
        for (const [robotId, h] of healthMap.entries()) {
            if (!h.lastActiveDate || h.lastActiveDate >= cutoff) continue;
            const m = robotMeta.get(robotId);
            if (!m) continue;
            out.push({ robotId, robotName: m.robotName, client: m.client, siteName: m.siteName, lastDrove: h.lastActiveDate, daysDark: daysBetween(h.lastActiveDate, maxDate) });
        }
        return out.sort((a, b) => a.daysDark - b.daysDark);
    }, [healthMap, robotMeta, maxDate]);

    /* ---- idle robots (powered on, never driven) ---- */
    const idleRobots = useMemo(() => {
        const byRobot = new Map();
        if (!raw || !raw.sites) return [];
        for (const [siteId, site] of Object.entries(raw.sites)) {
            if (!site || site.error) continue;
            const clientName = normalizeClientName(site.client || "Unknown", site.site_name || siteId);
            const siteName = site.site_name || siteId;
            const siteAudience = classifyAudience(clientName, siteName);
            if (siteAudience === "internal") continue;
            if (audience !== "all" && siteAudience !== audience) continue;
            if (clientFilter !== "All" && clientName !== clientFilter) continue;
            if (siteFilter !== "All" && siteName !== siteFilter) continue;
            for (const robot of site.robots || []) {
                const robotId = robot.robot_id;
                const robotName = robot.name || robotId;
                if (robotFilter !== "All" && robotName !== robotFilter) continue;
                const daily = Array.isArray(robot.daily) ? robot.daily : [];
                const inRangeDaily = daily.filter((d) => { const date = d.date; if (!dateFrom || !dateTo) return true; return date >= dateFrom && date <= dateTo; });
                const uptimeS = inRangeDaily.reduce((sum, d) => sum + (d.uptime_s || 0), 0);
                if (!uptimeS) continue;
                const everDrove = daily.some((d) => (d.distance_m || 0) > 0 || (d.moving_time_s || 0) > 0);
                if (everDrove) continue;
                const uptimeDays = inRangeDaily.filter((d) => (d.uptime_s || 0) > 0);
                const firstSeen = uptimeDays[0]?.date || null;
                const lastSeen = uptimeDays[uptimeDays.length - 1]?.date || null;
                let entry = byRobot.get(robotId);
                if (!entry) { entry = { _key: robotId, robotId, robotName, clientSet: new Set(), siteSet: new Set(), uptimeH: 0, activeDays: 0, firstSeen, lastSeen }; byRobot.set(robotId, entry); }
                entry.clientSet.add(clientName); entry.siteSet.add(siteName); entry.uptimeH += uptimeS / 3600; entry.activeDays += uptimeDays.length;
                if (!entry.firstSeen || (firstSeen && firstSeen < entry.firstSeen)) entry.firstSeen = firstSeen;
                if (!entry.lastSeen || (lastSeen && lastSeen > entry.lastSeen)) entry.lastSeen = lastSeen;
            }
        }
        return Array.from(byRobot.values()).map((r) => ({ ...r, clients: Array.from(r.clientSet).sort().join(", "), sites: Array.from(r.siteSet).sort().join(", "), siteCount: r.siteSet.size })).sort((a, b) => b.uptimeH - a.uptimeH);
    }, [raw, audience, clientFilter, siteFilter, robotFilter, dateFrom, dateTo]);

    /* ---- chart data ---- */
    const fullChartData = useMemo(() => buildDailySeries(scope), [scope]);
    const mainChartData = useMemo(() => {
        if (!dateFrom || !dateTo) return fullChartData;
        return fullChartData.filter((d) => d.date >= dateFrom && d.date <= dateTo);
    }, [fullChartData, dateFrom, dateTo]);

    const brushIndexes = useMemo(() => {
        if (!fullChartData.length) return { startIndex: 0, endIndex: 0 };
        const startIndex = Math.max(0, fullChartData.findIndex((d) => d.date >= (dateFrom || fullChartData[0].date)));
        let endIndex = -1;
        for (let i = fullChartData.length - 1; i >= 0; i--) { if (fullChartData[i].date <= (dateTo || fullChartData[fullChartData.length - 1].date)) { endIndex = i; break; } }
        return { startIndex, endIndex: endIndex === -1 ? fullChartData.length - 1 : endIndex };
    }, [fullChartData, dateFrom, dateTo]);

    /* ---- chart markers (anomalies on chart) ---- */
    const chartMarkers = useMemo(() => detectChartMarkers(mainChartData, metric), [mainChartData, metric]);

    const applyQuickRange = (days, label) => {
        if (!dateBounds.max) return;
        setQuickRange(label);
        if (days === null) { setDateFrom(dateBounds.min); setDateTo(dateBounds.max); return; }
        const newFrom = addDays(dateBounds.max, -(days - 1));
        setDateFrom(newFrom < dateBounds.min ? dateBounds.min : newFrom);
        setDateTo(dateBounds.max);
    };

    const handleChartMouseDown = (e) => { if (!e || !e.activeLabel) return; setDragStart(e.activeLabel); setDragEnd(e.activeLabel); };
    const handleChartMouseMove = (e) => { if (dragStart && e && e.activeLabel) setDragEnd(e.activeLabel); };
    const handleChartMouseUp = () => {
        if (dragStart && dragEnd && dragStart !== dragEnd) {
            const [a, b] = dragStart < dragEnd ? [dragStart, dragEnd] : [dragEnd, dragStart];
            setDateFrom(a); setDateTo(b);
        }
        setDragStart(null); setDragEnd(null);
    };
    const handleBrushChange = (range) => {
        if (!range || typeof range.startIndex !== "number" || typeof range.endIndex !== "number" || !fullChartData.length) return;
        const start = fullChartData[range.startIndex]; const end = fullChartData[range.endIndex];
        if (!start || !end) return;
        setDateFrom(start.date); setDateTo(end.date);
    };

    /* ---- click-to-drill navigation ---- */
    // Filters key on robotName (pre-existing) — two identically named robots at
    // different clients would collide here; acceptable for now.
    const onSelectRobot = (robotName) => {
        setRobotFilter(robotName);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const onShowOffline = () => {
        setInactiveOpen(true);
        setInactiveTab("offline");
        setTimeout(() => inactiveRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    };
    const onShowAttention = () => {
        attentionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const downloadCsv = () => {
        const header = ["client", "site", "robot_id", "robot_name", "km", "hours", "active_days", "rri", "first_active", "last_active"];
        const lines = [header.join(",")];
        for (const r of perRobot) {
            const rri = healthMap.get(r.robotId)?.rri;
            lines.push([r.client, r.siteName, r.robotId, JSON.stringify(r.robotName), (r.distance_m / 1000).toFixed(2), (r.moving_time_s / 3600).toFixed(2), r.activeDays, rri ?? "", r.firstActive || "", r.lastActive || ""].join(","));
        }
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `robot_stats_${dateFrom || "all"}_${dateTo || "all"}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    /* -------- header -------- */
    const HeaderBar = () => (
        <div className="flex items-start justify-between mb-6 gap-4">
            <div>
                <button onClick={onBack} className="mb-3 inline-flex items-center gap-2 text-sm transition-colors" style={{ color: C.grey1 }} onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)} onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}>
                    <ArrowLeft size={16} /> Back to site directory
                </button>
                <h1 className="text-3xl font-bold mb-1" style={{ color: C.offWhite }}>Robot fleet stats</h1>
                {raw && dateBounds.min && dateBounds.max && (
                    <p className="text-sm" style={{ color: C.grey2 }}>
                        Data from <span style={{ color: C.offWhite }}>{dateBounds.min}</span>
                        {lastUpdated && <> · Last updated <span style={{ color: C.offWhite }}>{lastUpdated.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span></>}
                        {" "}· Cloud sites only
                    </p>
                )}
            </div>
            <div className="flex items-center gap-2 text-sm">
                {userEmail && <span style={{ color: C.grey2 }} className="px-2">{userEmail}</span>}
                {onSignOut && (
                    <button onClick={onSignOut} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors" style={brandButton()} onMouseEnter={hoverNeon} onMouseLeave={unhoverNeon} title="Sign out">
                        <LogOut size={16} /> Sign out
                    </button>
                )}
            </div>
        </div>
    );

    /* -------- loading / error states -------- */
    if (loadError) {
        return (
            <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>
                <HeaderBar />
                <div className="rounded-2xl p-4" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
                    <h2 className="text-lg font-semibold mb-2">Couldn't load fleet stats</h2>
                    <p className="text-sm" style={{ color: C.grey1 }}>{loadError}</p>
                    <p className="text-sm mt-2" style={{ color: C.grey2 }}>Check that <code>robot_stats.json</code> is published to the portal bucket.</p>
                </div>
            </div>
        );
    }
    if (!raw) return <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>Loading fleet stats…</div>;

    const filtersActive = audience !== "customer" || clientFilter !== "All" || siteFilter !== "All" || robotFilter !== "All" || quickRange !== "30d";

    return (
        <div className="min-h-screen px-6 py-10" style={{ background: PAGE_BG, color: C.offWhite }}>
            <style>{datepickerStyles}</style>
            {/* Constrained measure — full-bleed content is what reads as "messy" on wide screens */}
            <div className="max-w-[1240px] mx-auto">
            <HeaderBar />

            {/* ── Compact filter bar ── */}
            {dateBounds.min && dateBounds.max && (
                <div className="rounded-xl px-3 py-3 mb-8" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Audience lens: who the fleet story is about. Styled
                            like the dropdowns beside it — label cell + options. */}
                        <div className="flex items-stretch rounded-xl overflow-hidden" style={{ border: "1px solid rgba(205,214,217,0.16)", backgroundColor: "#020b16" }}>
                            <span
                                className="px-4 py-2.5 text-sm select-none whitespace-nowrap"
                                style={{ backgroundColor: "rgba(255,255,255,0.02)", color: C.grey1, borderRight: "1px solid rgba(205,214,217,0.16)" }}
                            >
                                View
                            </span>
                            {AUDIENCE_OPTIONS.map((opt, i) => {
                                const isActive = audience === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        onClick={() => setAudience(opt.key)}
                                        className="px-3.5 py-2.5 text-sm font-semibold transition-colors"
                                        title={opt.hint}
                                        style={{
                                            backgroundColor: isActive ? C.strongBlue : "transparent",
                                            color: isActive ? C.offWhite : C.grey2,
                                            borderLeft: i > 0 ? "1px solid rgba(205,214,217,0.12)" : "none",
                                        }}
                                        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = C.neonGreen; }}
                                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = C.grey2; }}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{ width: 230 }}><Dropdown label="Client" value={clientFilter} options={clientOptions} onChange={setClientFilter} /></div>
                        <div style={{ width: 230 }}><Dropdown label="Site" value={siteFilter} options={siteOptions} onChange={setSiteFilter} /></div>
                        <div style={{ width: 230 }}><Dropdown label="Robot" value={robotFilter} options={robotOptions} onChange={setRobotFilter} /></div>
                        <div className="flex gap-1.5">
                            {[{ label: "7d", days: 7 }, { label: "30d", days: 30 }, { label: "90d", days: 90 }, { label: "1y", days: 365 }, { label: "All", days: null }].map(({ label, days }) => (
                                <QuickRangeButton key={label} label={label} active={quickRange === label} onClick={() => applyQuickRange(days, label)} />
                            ))}
                        </div>
                        {[{ label: "From", value: dateFrom || dateBounds.min, onChange: setDateFrom, min: dateBounds.min, max: dateBounds.max }, { label: "To", value: dateTo || dateBounds.max, onChange: setDateTo, min: dateFrom || dateBounds.min, max: dateBounds.max }].map(({ label, value, onChange, min, max }) => (
                            <div key={label} className="flex items-center gap-2 px-3 rounded-xl" style={{ backgroundColor: "#020b16", border: "1px solid rgba(205,214,217,0.16)", color: C.offWhite, height: 40, width: 155 }} title={label}>
                                <CalendarDays size={14} style={{ color: C.grey2, flexShrink: 0 }} />
                                <DatePicker
                                    selected={isoToDate(value)}
                                    onChange={(date) => onChange(dateToIso(date))}
                                    minDate={isoToDate(min)}
                                    maxDate={isoToDate(max)}
                                    dateFormat="dd/MM/yyyy"
                                    className="bg-transparent outline-none w-full text-sm font-medium"
                                    popperPlacement="bottom-start"
                                    calendarClassName="wheelme-datepicker"
                                />
                            </div>
                        ))}
                    </div>
                    {/* Scope echo — only when the user has narrowed something */}
                    {filtersActive && (
                        <div className="mt-2.5 px-1 text-xs flex items-center gap-1.5 flex-wrap" style={{ color: C.grey2 }}>
                            Scope:{" "}
                            <span style={{ color: C.grey1, fontWeight: 600 }}>
                                {[
                                    audience !== "customer" && (AUDIENCE_OPTIONS.find((o) => o.key === audience)?.label || audience),
                                    clientFilter !== "All" && clientFilter,
                                    siteFilter !== "All" && siteFilter,
                                    robotFilter !== "All" && robotFilter,
                                ].filter(Boolean).join(" · ") || "all customers"}
                            </span>
                            {" · "}{fmtInt(totals.robots)} robot{totals.robots === 1 ? "" : "s"} · {dateFrom} → {dateTo}
                            <button
                                onClick={() => { setAudience("customer"); setClientFilter("All"); setSiteFilter("All"); setRobotFilter("All"); applyQuickRange(30, "30d"); }}
                                className="ml-1 underline decoration-dotted underline-offset-2 transition-colors"
                                style={{ color: C.grey2 }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey2)}
                            >
                                reset
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Drill-down banner: obvious way back to the whole-fleet view ── */}
            {robotFilter !== "All" && (
                <div
                    className="flex items-center gap-3 mb-6 rounded-xl px-4 py-2.5"
                    style={{ backgroundColor: "rgba(9,70,95,0.35)", border: `1px solid ${C.strongBlue}` }}
                >
                    <Bot size={16} style={{ color: C.grey1, flexShrink: 0 }} />
                    <span className="text-sm min-w-0 truncate" style={{ color: C.grey1 }}>
                        Viewing a single robot: <span style={{ color: C.offWhite, fontWeight: 600 }}>{robotFilter}</span> — every number on this page is scoped to it.
                    </span>
                    <button
                        onClick={() => setRobotFilter("All")}
                        className="ml-auto shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                        style={brandButton()}
                        onMouseEnter={hoverNeon}
                        onMouseLeave={unhoverNeon}
                    >
                        <X size={14} /> Back to whole fleet
                    </button>
                </div>
            )}

            {/* ── 1. Headline + fleet board (the summary layer) ── */}
            <HeadlineSentence headline={headline} onAction={(a) => a === "attention" && onShowAttention()} />

            <FleetBoard board={boardData} offlineRecentCount={recentlyOffline.length} onSelectRobot={onSelectRobot} onShowOffline={onShowOffline} />

            {/* ── 2. Needs attention ── */}
            <AttentionSection
                inField={attentionInField}
                recentlyOffline={recentlyOffline}
                infoEntries={infoAttention}
                clientFindings={clientTrendFindings}
                healthyActiveCount={liveRobots.count}
                onSelectRobot={onSelectRobot}
                sectionRef={attentionRef}
            />

            <div className="my-6" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

            {/* ── 3. How we got here ── */}
            <Overline>How we got here</Overline>
            <TrendCards trends={trends} dateFrom={dateFrom} dateTo={dateTo} />

            {positiveFindings.length > 0 && (
                <div className="flex flex-col gap-1.5 mb-6">
                    {positiveFindings.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm" style={{ color: C.grey1 }}>
                            <TrendingUp size={14} style={{ color: C.neonGreen, flexShrink: 0 }} />
                            <span>
                                <span style={{ fontWeight: 600, color: C.offWhite }}>{f.title}</span>
                                {f.detail && <span style={{ color: C.grey2 }}> — {f.detail}</span>}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <DailyActivityChart
                fullChartData={fullChartData}
                mainChartData={mainChartData}
                metric={metric}
                setMetric={setMetric}
                brushIndexes={brushIndexes}
                handleBrushChange={handleBrushChange}
                dragStart={dragStart}
                dragEnd={dragEnd}
                handleChartMouseDown={handleChartMouseDown}
                handleChartMouseMove={handleChartMouseMove}
                handleChartMouseUp={handleChartMouseUp}
                chartMarkers={chartMarkers}
            />

            <div className="my-6" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

            {/* ── 4. Details on demand ── */}
            <Overline>Details on demand</Overline>

            {/* All robots — activity & readiness */}
            <RobotHealthSection perRobot={perRobot} healthMap={healthMap} onSelectRobot={onSelectRobot} />

            {/* Inactive & idle (merged: went quiet / offline / never driven / no activity in period) */}
            <InactiveRobotsSection
                data={{
                    quiet: mostChanged.becameInactive,
                    offline: offlineRobots,
                    idle: idleRobots,
                    period: inactiveRobots,
                }}
                open={inactiveOpen}
                setOpen={setInactiveOpen}
                activeTab={inactiveTab}
                setActiveTab={setInactiveTab}
                onSelectRobot={onSelectRobot}
                sectionRef={inactiveRef}
            />

            {/* Movers */}
            <MostChangedSection mostChanged={mostChanged} />

            {/* Per-site totals */}
            {siteFilter === "All" && perSite.length > 0 && (
                <div className="mb-8">
                    <DetailToggle open={showSiteTable} onToggle={() => setShowSiteTable((v) => !v)} icon={Building2}>
                        per-site totals
                    </DetailToggle>
                    {showSiteTable && (
                        <div className="mt-3">
                            <DataTable
                                columns={[
                                    { key: "client", label: "Client" },
                                    { key: "siteName", label: "Site" },
                                    { key: "robots", label: "robots", align: "right" },
                                    { key: "km", label: "km", align: "right", render: (s) => fmtNum(s.distance_m / 1000, 0) },
                                    { key: "hours", label: "hours", align: "right", render: (s) => fmtNum(s.moving_time_s / 3600, 0) },
                                    { key: "kmPerRobot", label: "km / robot", align: "right", render: (s) => fmtNum(s.kmPerRobot, 1) },
                                    { key: "firstActive", label: "first", render: (s) => s.firstActive || "—" },
                                    { key: "lastActive", label: "last", render: (s) => s.lastActive || "—" },
                                ]}
                                rows={perSite.map((s) => ({ ...s, _key: s.siteName }))}
                            />
                        </div>
                    )}
                </div>
            )}

            <div className="my-6" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} />

            {/* CSV export */}
            <button onClick={downloadCsv} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors" style={brandButton()} onMouseEnter={hoverNeon} onMouseLeave={unhoverNeon}>
                <Download size={16} /> Download current view as CSV
            </button>
            </div>
        </div>
    );
}
